import type { TextAlignmentModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { Toggle } from "@replo/design-system/components/toggle/Toggle";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { AlignCenter, AlignJustify, AlignLeft, AlignRight } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

const VALID_ALIGNMENTS = ["left", "center", "right", "justify"] as const;
type ValidAlignment = (typeof VALID_ALIGNMENTS)[number];

/**
 * NOTE (Kurt, 2025-07-24): Maps CSS text-align values to display values for the UI
 * CSS supports logical values (start/end) that are direction-aware, but our UI
 * only displays physical directions (left/right). We map start→left and end→right
 * assuming LTR text direction. This is acceptable since the editor primarily
 * targets LTR languages and users can still select the desired visual alignment
 * from the UI.
 *
 * If users complain about this and are using RTL languages we can add support for
 * the logical values to the UI and account for RTL text alignment.
 */
const getDisplayValue = (cssValue: string | null): string | null => {
  if (!cssValue) {
    return null;
  }

  switch (cssValue) {
    case "start":
      return "left";
    case "end":
      return "right";
    default:
      return cssValue;
  }
};

interface TextAlignmentModifierProps {
  onChange: (modifiers: TextAlignmentModifierValue[]) => void;
}

const TextAlignmentModifierComponent: React.FC<TextAlignmentModifierProps> = ({
  onChange,
}) => {
  const rawValue =
    useOptimisticModifierValue({
      modifierType: { type: "alignment", value: "left" },
      extractor: (elementDesignation) => {
        return elementDesignation.element.styleOverrides?.textAlign ?? null;
      },
    }) ?? null;

  const currentValue = getDisplayValue(rawValue);

  const [alignment, setAlignment] = useOverridableState(
    currentValue ?? "left",
    (newAlignment: string) => {
      onChange([
        {
          type: "alignment" as const,
          value: newAlignment as ValidAlignment,
        },
      ]);
    },
  );

  return (
    <div className="alignment-modifier">
      <ToggleGroup
        value={[alignment]}
        onValueChange={(newValue) => {
          if (newValue.length > 0) {
            setAlignment(newValue[0]!);
          }
        }}
        className="w-full"
      >
        <Toggle
          value="left"
          size="sm"
          icon
          aria-label="Align Left"
          className="flex-1"
        >
          <AlignLeft size={16} />
        </Toggle>
        <Toggle
          value="center"
          size="sm"
          icon
          aria-label="Align Center"
          className="flex-1"
        >
          <AlignCenter size={16} />
        </Toggle>
        <Toggle
          value="right"
          size="sm"
          icon
          aria-label="Align Right"
          className="flex-1"
        >
          <AlignRight size={16} />
        </Toggle>
        <Toggle
          value="justify"
          size="sm"
          icon
          aria-label="Justify"
          className="flex-1"
        >
          <AlignJustify size={16} />
        </Toggle>
      </ToggleGroup>
    </div>
  );
};

export default TextAlignmentModifierComponent;
